// Чистые функции страниц «Инструментов 3»: подбор названий и файлов из ответа сервиса обработки.

const MODEL_RE = /^seg_(\d{2})\.(stl|vtk)$/;
const MAIN_FILES = {
  segment: ['report.txt', 'segmentation.nii.gz', 'vtk_files.zip', 'classes.json', 'report.json'],
  report: ['report.txt', 'report.json', 'segmentation.nii.gz'],
};

export const isRu = (language) => String(language || '').toLowerCase().startsWith('ru');

export const structureName = (structure, language) => {
  if (!structure) return '';
  const name = isRu(language) ? structure.name_ru : structure.name_en;
  return name || structure.name_en || structure.name_ru || String(structure.label);
};

export const warningText = (warning, language) => {
  if (!warning) return '';
  return (isRu(language) ? warning.message_ru : warning.message_en) || warning.message_en || warning.message_ru || '';
};

/** Основные файлы (в заданном порядке) и модели по структурам (seg_NN.stl / seg_NN.vtk). */
export const splitFiles = (names, kind) => ({
  main: (MAIN_FILES[kind] || []).filter((n) => names.includes(n)),
  models: names.filter((n) => MODEL_RE.test(n)),
});

/** Подпись кнопки скачивания модели: «Нижняя челюсть (VTK)». */
export const modelLabel = (fileName, structures, language) => {
  const m = MODEL_RE.exec(fileName);
  if (!m) return fileName;
  const found = (structures || []).find((s) => s.label === Number(m[1]));
  return `${found ? structureName(found, language) : `№ ${Number(m[1])}`} (${m[2].toUpperCase()})`;
};

export const formatTeeth = (fdi) => (Array.isArray(fdi) ? [...fdi].sort((a, b) => a - b).join(', ') : '');

// ---- 3D-просмотрщик результата сегментации ----

const MESH_RE = /^seg_(\d{2})\.(stl|vtk)$/;
// Порядок загрузки: сначала то, что нужно для ориентации (кости), потом каналы и реставрации, зубы в конце.
const LOAD_ORDER = ['bone', 'canal', 'restoration', 'sinus', 'airway', 'tooth', 'pulp'];
const GROUP_COLORS = {
  bone: '#e8e2d4', tooth: '#f4efe0', canal: '#e03a3a', sinus: '#4aa8d8',
  airway: '#5a7fd6', restoration: '#a35bd6', pulp: '#e8508f',
};

/** Какой файл модели брать для метки: облегчённый VTK, а при его отсутствии STL. */
export const meshFileByLabel = (names) => {
  const found = {};
  (names || []).forEach((n) => {
    const m = MESH_RE.exec(n);
    if (!m) return;
    const label = Number(m[1]);
    const prev = found[label];
    if (!prev || (m[2] === 'vtk' && prev.format === 'stl')) found[label] = { name: n, format: m[2] };
  });
  return found;
};

/** Структуры, у которых есть модель, в порядке загрузки. */
export const loadQueue = (structures, meshes) => {
  const rank = (g) => { const i = LOAD_ORDER.indexOf(g); return i < 0 ? LOAD_ORDER.length : i; };
  return (structures || [])
    .filter((s) => meshes[s.label])
    .map((s) => ({ ...s, file: meshes[s.label] }))
    .sort((a, b) => rank(a.group) - rank(b.group) || a.label - b.label);
};

export const groupColor = (group) => GROUP_COLORS[group] || '#cccccc';

/** Цвет структуры: из classes.json сервиса, иначе по группе. */
export const structureColor = (structure, classesByLabel) => (
  (classesByLabel && classesByLabel[structure.label]?.color) || groupColor(structure.group)
);

/** Разбор classes.json: {classes:[{label,color,...}]} -> {label: запись}. */
export const indexClasses = (data) => {
  const list = Array.isArray(data) ? data : data?.classes;
  const out = {};
  (Array.isArray(list) ? list : []).forEach((c) => { if (c && Number.isFinite(c.label)) out[c.label] = c; });
  return out;
};

/**
 * Вершины сервиса идут в порядке (z, y, x) в миллиметрах, оси массива: x к правому, y к переднему, z к верхнему.
 * В сцене three.js (Y вверх) кладём (x, z, -y): это поворот без отражения, пациент смотрит в сторону -Z,
 * камера стоит перед ним на -Z, правая сторона пациента оказывается слева на экране, как на радиологическом снимке.
 */
export const toSceneInPlace = (positions) => {
  for (let i = 0; i + 2 < positions.length; i += 3) {
    const z = positions[i]; const y = positions[i + 1]; const x = positions[i + 2];
    positions[i] = x; positions[i + 1] = z; positions[i + 2] = -y;
  }
  return positions;
};

export const VIEWER_GROUPS = ['bone', 'tooth', 'canal', 'sinus', 'airway', 'restoration', 'pulp'];
